import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Games as GamesIcon,
  Shuffle as ShuffleIcon,
  Quiz as QuizIcon,
  Headphones as HeadphonesIcon,
  Bookmark as BookmarkIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, getDocs } from 'firebase/firestore'
import toast from 'react-hot-toast'
import WordMatchLevelSelection from './WordMatchLevelSelection'
import WordScrambleLevelSelection from './WordScrambleLevelSelection'
import QuizLevelSelection from './QuizLevelSelection'
import ListeningWriteLevelSelection from './ListeningWriteLevelSelection'
import SavedWordsGames from './SavedWordsGames'
import SpaceTheme from '../components/SpaceTheme'

const GameSelection = ({ userData, userLevel, onBack }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [currentLevel, setCurrentLevel] = useState(userLevel || 'A1')
  const [currentPage, setCurrentPage] = useState('game-selection')

  useEffect(() => {
    if (user && userData) {
      setCurrentLevel(userLevel || userData.level || 'A1')
      setLoading(false)
    }
  }, [user, userData, userLevel])

  const gameItems = [
    {
      id: 'word-match',
      title: 'Word Match',
      description: 'Kelimeleri eşleştir',
      icon: <GamesIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      color: 'primary',
      action: () => handleGameClick('word-match')
    },
    {
      id: 'word-scramble',
      title: 'Word Scramble',
      description: 'Karışık kelimeleri çöz',
      icon: <ShuffleIcon sx={{ fontSize: 48, color: 'secondary.main' }} />,
      color: 'secondary',
      action: () => handleGameClick('word-scramble')
    },
    {
      id: 'quiz',
      title: 'Quiz',
      description: 'Bilgi yarışması',
      icon: <QuizIcon sx={{ fontSize: 48, color: 'success.main' }} />,
      color: 'success',
      action: () => handleGameClick('quiz')
    },
    {
      id: 'listening-write',
      title: 'Listening Write',
      description: 'Dinlediğini yaz',
      icon: <HeadphonesIcon sx={{ fontSize: 48, color: 'warning.main' }} />,
      color: 'warning',
      action: () => handleGameClick('listening-write')
    },
    {
      id: 'saved-words',
      title: 'Saved Words Games',
      description: 'Kaydedilen kelimelerle oyna',
      icon: <BookmarkIcon sx={{ fontSize: 48, color: 'info.main' }} />,
      color: 'info',
      action: () => handleGameClick('saved-words')
    }
  ]

  const handleGameClick = (gameType) => {
    if (!user) {
      toast.error('Oyun oynamak için giriş yapmalısınız')
      return
    }

    console.log('GameSelection handleGameClick:', { gameType, currentLevel })

    switch (gameType) {
      case 'word-match':
        setCurrentPage('word-match-level-selection')
        break
      case 'word-scramble':
        setCurrentPage('word-scramble-level-selection')
        break
      case 'quiz':
        setCurrentPage('quiz-level-selection')
        break
      case 'listening-write':
        setCurrentPage('listening-write-level-selection')
        break
      case 'saved-words':
        setCurrentPage('saved-words-games')
        break
      default:
        console.log('Unknown game type:', gameType)
    }
  }

  const handleBackToGameSelection = (data) => {
    if (data && data.page) {
      setCurrentPage(data.page)
    } else {
      setCurrentPage('game-selection')
    }
  }

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onBack}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Oyunlar
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        </Container>
      </Box>
    )
  }

  // Word Match Level Selection sayfasını göster
  if (currentPage === 'word-match-level-selection') {
    return <WordMatchLevelSelection 
      userData={userData} 
      userLevel={currentLevel}
      onBack={handleBackToGameSelection} 
    />
  }

  // Word Scramble Level Selection sayfasını göster
  if (currentPage === 'word-scramble-level-selection') {
    return <WordScrambleLevelSelection 
      userData={userData} 
      userLevel={currentLevel}
      onBack={handleBackToGameSelection} 
    />
  }

  // Quiz Level Selection sayfasını göster
  if (currentPage === 'quiz-level-selection') {
    return <QuizLevelSelection 
      userData={userData} 
      userLevel={currentLevel}
      onBack={handleBackToGameSelection} 
    />
  }

  // Listening Write Level Selection sayfasını göster
  if (currentPage === 'listening-write-level-selection') {
    return <ListeningWriteLevelSelection 
      userData={userData} 
      userLevel={currentLevel}
      onBack={handleBackToGameSelection} 
    />
  }

  // Saved Words Games sayfasını göster
  if (currentPage === 'saved-words-games') {
    return <SavedWordsGames 
      userData={userData} 
      userLevel={currentLevel}
      onBack={handleBackToGameSelection} 
    />
  }

  return (
    <SpaceTheme>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Oyunlar
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {/* Header Card - Reading sayfasındaki gibi */}
        <Card sx={{ 
          mx: 2, 
          mt: 2, 
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <GamesIcon sx={{ fontSize: 56, color: '#1976d2', mb: 3 }} />
            <Typography variant="h3" sx={{ 
              color: '#1976d2', 
              fontWeight: 'bold',
              mb: 1
            }}>
              Oyunlar
            </Typography>
            <Typography variant="h6" sx={{ color: '#1976d2', opacity: 0.8 }}>
              Seviye: {currentLevel}
            </Typography>
          </CardContent>
        </Card>

        {/* Games Grid */}
        <Grid container spacing={3} sx={{ mt: 2, px: 2 }}>
          {gameItems.map((game) => (
            <Grid item xs={12} sm={6} md={4} key={game.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                    backgroundColor: 'rgba(255, 255, 255, 1)'
                  }
                }}
                onClick={game.action}
              >
                <CardContent sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <Box sx={{ mb: 2 }}>
                    {game.icon}
                  </Box>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 'bold',
                    color: '#1976d2',
                    mb: 1
                  }}>
                    {game.title}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary',
                    opacity: 0.8
                  }}>
                    {game.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>


      </Container>
    </SpaceTheme>
  )
}

export default GameSelection
